import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { BookOpen, Clock, Settings as SettingsIcon, HandHeart, MessageSquarePlus, MessageCircle, Search, LibraryBig, MessageSquare, Sparkles, Users, Mic, Menu, Shield, LogOut } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import SettingsContent from "@/components/SettingsContent";
import HelpAssistant from "@/components/HelpAssistant";
import CreditMeter from "@/components/CreditMeter";
import TrialBanner from "@/components/TrialBanner";
import EthosOnboarding from "@/components/EthosOnboarding";
import { useAuth } from "@/context/AuthContext";
import { useDeeperNav } from "@/context/deeperNav";
import useIsDesktop from "@/hooks/useIsDesktop";
import { api } from "@/lib/kfmApi";

const TIER_LABEL = { free: "Free plan", basic: "Basic plan", pro: "Pro plan", team: "Team plan" };

// Set to false to revert desktop Settings to a full page (mobile is unaffected).
const DESKTOP_SETTINGS_DRAWER = true;

// Desktop left-sidebar primary nav.
const sideTabs = [
  { to: "/study", label: "Study", icon: BookOpen, testid: "study" },
  { to: "/rooms", label: "Study together", icon: Users, testid: "rooms" },
  { to: "/journal", label: "Prayers", icon: HandHeart, testid: "journal" },
  { to: "/voice-notes", label: "Voice Notes", icon: Mic, testid: "voice-notes" },
  { to: "/history", label: "My Studies", icon: Clock, testid: "history" },
  { to: "/search", label: "Search", icon: Search, testid: "search" },
  { to: "/resources", label: "Resources", icon: LibraryBig, testid: "resources" },
];

// Mobile bottom tab bar. Space is tight, so it shows the most-used destinations plus a
// "More" menu that opens a sheet with EVERY feature (mobile-first: nothing is desktop-only).
const mobileTabs = [
  { to: "/study", label: "Study", icon: BookOpen, testid: "tab-study" },
  { to: "/voice-notes", label: "Voice", icon: Mic, testid: "tab-voice-notes" },
  { to: "/journal", label: "Prayers", icon: HandHeart, testid: "tab-journal" },
  { label: "More", icon: Menu, testid: "tab-more", more: true },
];

const SIDE_BASE = "flex items-center gap-3 px-3 py-2.5 rounded-r-lg text-sm font-medium border-l-2 leading-tight transition-colors";
const SIDE_INACTIVE = "border-transparent text-slate-400 hover:text-[#00E5FF] hover:bg-[#10222A]/30";
const SIDE_ACTIVE = "border-[#00E5FF] text-[#00E5FF] bg-[#10222A]/40";

export default function Layout() {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const { user, logout } = useAuth();
  const { requestOpen } = useDeeperNav();
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const location = useLocation();
  const tier = user?.tier || "free";
  const isBeta = !!user?.beta_tester;
  // Beta users see the "Pro features unlocked" badge instead. Everyone else gets a link to
  // billing — labelled "Upgrade" for free users (a nudge) and "Manage" for paying users
  // (so subscribers can change or cancel their plan).
  const showUpgrade = !isBeta;
  const planCta = tier === "free" ? "Upgrade" : "Manage";
  const planCtaFull = tier === "free" ? "Upgrade your plan" : "Manage your plan";

  const onStudySurface =
    location.pathname === "/study" || location.pathname.startsWith("/study/");
  const goDeeper = () => {
    if (!onStudySurface) navigate("/study");
    requestOpen(); // opens now if the chat is mounted, else the moment it mounts
  };
  const doLogout = async () => {
    await logout();
    navigate("/login");
  };
  const renderMobileTab = ({ to, label, icon: Icon, testid, chat, more }) => {
    if (chat || more) {
      const active = chat ? helpOpen : moreOpen;
      const onClick = () => (chat ? setHelpOpen(true) : setMoreOpen(true));
      return (
        <button
          key={testid}
          data-testid={testid}
          onClick={onClick}
          className={`flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors duration-300 ${
            active ? "text-[#00E5FF]" : "text-slate-500 hover:text-slate-300"
          }`}
        >
          <Icon className="w-6 h-6" strokeWidth={1.5} />
          {label}
        </button>
      );
    }
    return (
    <NavLink
      key={to}
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors duration-300 ${
          isActive ? "text-[#00E5FF]" : "text-slate-500 hover:text-slate-300"
        }`
      }
    >
      <Icon className="w-6 h-6" strokeWidth={1.5} />
      {label}
    </NavLink>
    );
  };

  // Every authenticated feature — surfaced in the mobile "More" sheet so nothing is desktop-only.
  const moreLinks = [
    { to: "/study", label: "Study", icon: BookOpen },
    { to: "/rooms", label: "Study together", icon: Users },
    { to: "/journal", label: "Prayers", icon: HandHeart },
    { to: "/voice-notes", label: "Voice Notes", icon: Mic },
    { to: "/history", label: "My Studies", icon: Clock },
    { to: "/search", label: "Search", icon: Search },
    { to: "/resources", label: "Resources", icon: LibraryBig },
    { to: "/settings", label: "Settings", icon: SettingsIcon },
    { to: "/support", label: "Feedback", icon: MessageSquarePlus },
  ];

  return (
    <div className="min-h-screen bg-[#07090E]">
      <EthosOnboarding userId={user?.id} />
      {/* Desktop left sidebar (wide AND tall viewports only) */}
      <aside className={`${isDesktop ? "flex" : "hidden"} fixed left-0 top-0 h-screen w-[200px] flex-col bg-[#0E131F]/60 backdrop-blur-xl border-r border-[#1E273D] z-40 px-4 py-7`}>
        <div className="flex items-center gap-2 px-2 mb-8">
          <img src="/openword-logo-horizontal.png" alt="OpenWord AI" className="h-6" />
        </div>
        {/* Always visible the instant the app opens — not gated on an active study session */}
        <button
          data-testid="side-godeeper"
          onClick={goDeeper}
          className="flex items-center gap-3 px-3 py-2.5 mb-6 rounded-lg text-sm font-semibold bg-[#00E5FF] text-[#07090E] hover:bg-[#00B8CC] transition-colors"
        >
          <MessageCircle className="w-5 h-5 shrink-0" strokeWidth={1.75} />
          Go Deeper
        </button>
        <nav className="flex flex-col gap-1">
          {sideTabs.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`side-tab-${testid}`}
              className={({ isActive }) => `${SIDE_BASE} ${isActive ? SIDE_ACTIVE : SIDE_INACTIVE}`}
            >
              <Icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto flex flex-col gap-1">
          <CreditMeter className="mb-2" />
          {isBeta && (
            <div
              data-testid="side-beta-badge"
              className="flex items-center gap-3 px-3 py-2.5 mb-2 rounded-lg text-sm font-semibold bg-[#10222A] text-[#00B8CC] border border-[#00E5FF]/20"
            >
              <Sparkles className="w-5 h-5 shrink-0" strokeWidth={1.5} />
              <span className="flex flex-col leading-tight">
                <span>Beta access</span>
                <span className="text-[11px] font-normal text-[#00E5FF]" data-testid="side-upgrade-tier">Pro features unlocked</span>
              </span>
            </div>
          )}
          {showUpgrade && (
            <NavLink
              to="/billing"
              data-testid="side-upgrade"
              className="flex items-center gap-3 px-3 py-2.5 mb-2 rounded-lg text-sm font-semibold bg-[#00E5FF] text-[#07090E] hover:bg-[#00B8CC] transition-colors"
            >
              <Sparkles className="w-5 h-5 shrink-0" strokeWidth={1.5} />
              <span className="flex flex-col leading-tight">
                <span>{planCta}</span>
                <span className="text-[11px] font-normal text-[#07090E]/70" data-testid="side-upgrade-tier">On {TIER_LABEL[tier]}</span>
              </span>
            </NavLink>
          )}
          {DESKTOP_SETTINGS_DRAWER ? (
            <button
              data-testid="side-tab-settings"
              onClick={() => setSettingsOpen(true)}
              className={`${SIDE_BASE} ${SIDE_INACTIVE} w-full text-left`}
            >
              <SettingsIcon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
              Settings
            </button>
          ) : (
            <NavLink
              to="/settings"
              data-testid="side-tab-settings"
              className={({ isActive }) => `${SIDE_BASE} ${isActive ? SIDE_ACTIVE : SIDE_INACTIVE}`}
            >
              <SettingsIcon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
              Settings
            </NavLink>
          )}
          <button
            data-testid="side-tab-help"
            onClick={() => setHelpOpen(true)}
            className={`${SIDE_BASE} ${SIDE_INACTIVE} w-full text-left`}
          >
            <MessageSquare className="w-5 h-5 shrink-0" strokeWidth={1.5} />
            Help &amp; Support
          </button>
          <NavLink
            to="/support"
            data-testid="side-tab-feedback"
            className={({ isActive }) => `${SIDE_BASE} ${isActive ? SIDE_ACTIVE : SIDE_INACTIVE}`}
          >
            <MessageSquarePlus className="w-5 h-5 shrink-0" strokeWidth={1.5} />
            Feedback, Suggestions?
          </NavLink>
          <button
            data-testid="side-tab-logout"
            onClick={doLogout}
            className={`${SIDE_BASE} border-transparent text-slate-400 hover:text-red-400 hover:bg-red-500/10 w-full text-left`}
          >
            <LogOut className="w-5 h-5 shrink-0" strokeWidth={1.5} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <div className={isDesktop ? "pl-[200px]" : ""}>
        <div className={`${isDesktop ? "max-w-none pb-0 shadow-none" : "max-w-md mx-auto pb-24 shadow-[0_0_40px_rgba(0,0,0,0.05)]"} min-h-screen bg-[#07090E] relative`}>
          <TrialBanner />
          <Outlet />
        </div>
      </div>

      {/* Mobile bottom tab bar (phones incl. landscape) with a prominent, centred "Go Deeper" action */}
      <nav className={`${isDesktop ? "hidden" : "flex"} fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-[#0E131F]/85 backdrop-blur-xl border-t border-[#1E273D] items-center h-20 px-2 z-50`}>
        <div className="flex-1 flex justify-around items-center">
          {mobileTabs.slice(0, 2).map(renderMobileTab)}
        </div>
        <button
          data-testid="tab-godeeper"
          onClick={goDeeper}
          aria-label="Go Deeper"
          className="shrink-0 mx-1 flex flex-col items-center justify-center -mt-7 focus:outline-none group"
        >
          <span className="w-14 h-14 rounded-full bg-[#00E5FF] text-[#07090E] flex items-center justify-center shadow-lg shadow-[#00E5FF]/40 ring-4 ring-[#07090E] group-hover:bg-[#00B8CC] group-active:scale-95 transition-all duration-200">
            <MessageCircle className="w-6 h-6" strokeWidth={1.75} />
          </span>
          <span className="text-[11px] font-semibold text-[#00E5FF] mt-1">Go Deeper</span>
        </button>
        <div className="flex-1 flex justify-around items-center">
          {mobileTabs.slice(2).map(renderMobileTab)}
        </div>
      </nav>

      {/* Desktop Settings slide-over (revertible via DESKTOP_SETTINGS_DRAWER) */}
      <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
        <SheetContent
          side="right"
          data-testid="settings-drawer"
          className="w-full sm:max-w-md overflow-y-auto bg-[#07090E] p-0"
        >
          <SheetTitle className="sr-only">Settings</SheetTitle>
          <SheetDescription className="sr-only">Your plan, preferences and account settings</SheetDescription>
          <div className="px-6 py-8">
            <SettingsContent onClose={() => setSettingsOpen(false)} />
          </div>
        </SheetContent>
      </Sheet>

      {/* Free help assistant — opened from the Help tab (mobile) or Help & Support (desktop) */}
      <HelpAssistant open={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* Mobile "More" menu — full feature list so every feature is reachable on mobile */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" data-testid="mobile-more-sheet" className="rounded-t-3xl bg-[#0E131F] p-0 max-h-[85vh] overflow-y-auto">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <SheetDescription className="sr-only">All features and pages</SheetDescription>
          <div className="px-5 pt-6 pb-10">
            <div className="text-xs font-medium uppercase tracking-[0.2em] text-[#00E5FF] mb-3">Menu</div>
            <div className="mb-3" onClick={() => setMoreOpen(false)}><CreditMeter /></div>
            {showUpgrade && (
              <NavLink to="/billing" data-testid="more-link-billing" onClick={() => setMoreOpen(false)}
                className="flex items-center gap-3 w-full mb-3 rounded-xl bg-[#00E5FF] text-[#07090E] px-4 py-3 text-sm font-semibold">
                <Sparkles className="w-5 h-5" strokeWidth={1.6} /> {planCtaFull}
              </NavLink>
            )}
            <div className="grid grid-cols-2 gap-2">
              {moreLinks.map(({ to, label, icon: Icon }) => (
                <NavLink key={to} to={to} data-testid={`more-link-${to.replace(/\//g, "") || "home"}`} onClick={() => setMoreOpen(false)}
                  className={({ isActive }) => `flex items-center gap-2.5 rounded-xl border px-3 py-3 text-sm font-medium transition-colors ${isActive ? "border-[#00E5FF] bg-[#10222A] text-[#00E5FF]" : "border-[#29344D] text-slate-400 hover:border-[#00E5FF]/40"}`}>
                  <Icon className="w-4 h-4 shrink-0" strokeWidth={1.6} /> {label}
                </NavLink>
              ))}
              <button data-testid="more-link-help" onClick={() => { setMoreOpen(false); setHelpOpen(true); }}
                className="flex items-center gap-2.5 rounded-xl border border-[#29344D] text-slate-400 hover:border-[#00E5FF]/40 px-3 py-3 text-sm font-medium transition-colors">
                <MessageSquare className="w-4 h-4 shrink-0" strokeWidth={1.6} /> Help &amp; Support
              </button>
              {user?.role === "admin" && (
                <NavLink to="/admin" data-testid="more-link-admin" onClick={() => setMoreOpen(false)}
                  className="flex items-center gap-2.5 rounded-xl border border-[#29344D] text-slate-400 hover:border-[#00E5FF]/40 px-3 py-3 text-sm font-medium transition-colors">
                  <Shield className="w-4 h-4 shrink-0" strokeWidth={1.6} /> Admin
                </NavLink>
              )}
              <button data-testid="more-link-logout" onClick={() => { setMoreOpen(false); doLogout(); }}
                className="flex items-center gap-2.5 rounded-xl border border-[#29344D] text-slate-400 hover:text-red-400 hover:border-red-500/40 px-3 py-3 text-sm font-medium transition-colors">
                <LogOut className="w-4 h-4 shrink-0" strokeWidth={1.6} /> Sign out
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
